// React
import { useMemo } from 'react'

// Libraries
import { useSelector, shallowEqual } from 'react-redux'
import { createSelector } from 'reselect'

/**
 * Custom equality function for Redux useSelector with Immutable.js collections.
 * Performs deep equality comparison using Immutable's equals() method instead
 * of the default reference equality (===) check.
 *
 * @param {Immutable.Collection} prev - Previous value from Redux store
 * @param {Immutable.Collection} next - Next value from Redux store
 * @returns {boolean} True if the collections are deeply equal
 *
 * @example
 * // Usage with useSelector
 * const data = useSelector(
 *   (state) => state.get('data'),
 *   equalityFn
 * );
 *
 * @example
 * // Handles nested Immutable structures
 * const nested = useSelector(
 *   (state) => state.getIn(['data', 'items']),
 *   equalityFn
 * );
 *
 * @see {@link https://immutable-js.com/docs/v4.3.4/Collection#equals}
 */
const equalityFn = (prev, next) => {
  // If both values are null/undefined, they're equal
  if (prev == null && next == null) {
    return true
  }

  // If only one value is null/undefined, they're not equal
  if (prev == null || next == null) {
    return false
  }

  // Check if both values are Immutable collections
  if (typeof prev?.equals === 'function') {
    return prev.equals(next)
  }

  // Fallback to strict equality for non-Immutable values
  return prev === next
}

/**
 * Custom hook to access ORM objects from Redux state with reselect memoization
 *
 * @param {string} entityName - The name of the ORM entity to access
 * @param {Function} [transformer] - Optional function to transform the data
 * @returns {any} The selected or transformed ORM data
 *
 * @example
 * // Basic usage
 * const locations = useOrm('locations')
 *
 * @example
 * // With transformer
 * const locationWithIdentifier = useOrm('locations', (locations) => {
 *   return locations.getById(1)
 * })
 *
 * @example
 * // With parameterized transformer
 * const getLocationById = (id) => (locations) => locations.getById(id)
 *
 * const location = useOrm('locations', getLocationById(id))
 *
 * @example
 * // With parameterized transformer and state accessor
 * const getCartById = (id) => (carts, state) => {
 *   return carts.getById(id || state.client.cartId)
 * }
 *
 * const cart = useOrm('carts', getCartById(id))
 */
const useOrm = (model, transformer = null, equalFn = equalityFn) => {
  const baseSelector = (state) => state.orm[model]

  const selector = useMemo(() => {
    return transformer ?
      createSelector([baseSelector], transformer) :
      baseSelector
  }, [transformer])

  return useSelector(selector, {
    equalityFn: equalFn
  })
}

export {
  shallowEqual,
  equalityFn,
  useOrm
}
